import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  TextField,
  Autocomplete,
  Avatar,
  Divider,
  Card,
  CardContent,
  IconButton,
  Collapse,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Close,
  Add,
  ExpandMore,
  ExpandLess,
  Assignment,
  CheckCircle,
  PersonAdd,
  TrendingUp,
  AddTask,
} from '@mui/icons-material';
import { Task, Role, Subtask } from '../types';
import { useProjectStore } from '../store/useProjectStore';
import { formatDate, getStatusInfo, getRoleColor } from '../utils/helpers';
import EditSubtaskModal from './EditSubtaskModal';

interface RoleDetailModalProps {
  open: boolean;
  onClose: () => void;
  role: Role;
}

interface NewSubtaskData {
  taskId: string;
  text: string;
  assignees: string[];
  startDate: string;
  expectedDate: string;
}

const RoleDetailModal: React.FC<RoleDetailModalProps> = ({ open, onClose, role }) => {
  const { project, addSubtask, addSubSubtask } = useProjectStore();
  const [showAddSubtask, setShowAddSubtask] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [newSubtaskData, setNewSubtaskData] = useState<NewSubtaskData>({
    taskId: '',
    text: '',
    assignees: [],
    startDate: '',
    expectedDate: '',
  });
  const [assigneeInput, setAssigneeInput] = useState('');
  
  // State for sub-subtask management
  const [expandedSubtasks, setExpandedSubtasks] = useState<Set<string>>(new Set());
  const [addingSubSubtask, setAddingSubSubtask] = useState<{ taskId: string; subtaskId: string } | null>(null);
  const [newSubSubtaskText, setNewSubSubtaskText] = useState('');
  const [editSubtaskModal, setEditSubtaskModal] = useState<{
    open: boolean;
    subtask: Subtask | null;
    taskId: string;
  }>({ open: false, subtask: null, taskId: '' });

  if (!project) return null;

  // Get all tasks and subtasks for this role
  const roleTasks = project.tasks.filter(task => task.roles.includes(role));
  const roleSubtasks = project.tasks.flatMap(task => 
    task.subtasks.filter(subtask => subtask.role === role)
  );

  // Get all tasks that could have this role's subtasks
  const availableTasks = project.tasks.filter(task => 
    task.roles.includes(role) || task.roles.length === 0
  );

  // Calculate statistics
  const totalTasks = roleTasks.length;
  const totalSubtasks = roleSubtasks.length;
  const completedSubtasks = roleSubtasks.filter(st => st.status === 'completed').length;
  const inProgressSubtasks = roleSubtasks.filter(st => st.status === 'in-progress').length;
  const pendingSubtasks = roleSubtasks.filter(st => st.status === 'pending').length;
  const reviewingSubtasks = roleSubtasks.filter(st => 
    ['pending-review', 'reviewing', 'review-failed'].includes(st.status)
  ).length;

  // Get unique personnel
  const personnel = new Set(roleSubtasks.flatMap(st => st.assignees));
  const personnelCount = personnel.size;

  // Get all existing assignees for autocomplete
  const allAssignees = Array.from(new Set(
    project.tasks.flatMap(task => 
      task.subtasks.flatMap(st => st.assignees)
    )
  ));

  // Calculate completion rate
  const completionRate = totalSubtasks > 0 
    ? Math.round((completedSubtasks / totalSubtasks) * 100) 
    : 0;

  const handleAddSubtask = () => {
    if (!selectedTask || !newSubtaskData.text || newSubtaskData.assignees.length === 0) {
      alert('請填寫所有必填欄位');
      return;
    }

    const newSubtask: Omit<Subtask, 'id'> = {
      text: newSubtaskData.text,
      role: role,
      assignees: newSubtaskData.assignees,
      status: 'pending',
      startDate: newSubtaskData.startDate ? new Date(newSubtaskData.startDate) : undefined,
      expectedDate: new Date(newSubtaskData.expectedDate),
      subSubtasks: [],
      discussionCount: 0,
      timeLogs: [],
      reviewHistory: [],
    };

    addSubtask(selectedTask.id, newSubtask);
    
    // Reset form
    setNewSubtaskData({
      taskId: '',
      text: '',
      assignees: [],
      startDate: '',
      expectedDate: '',
    });
    setSelectedTask(null);
    setAssigneeInput('');
    setShowAddSubtask(false);
  };

  const handleTaskSelect = (task: Task | null) => {
    setSelectedTask(task);
    if (task) {
      setNewSubtaskData({ ...newSubtaskData, taskId: task.id });
      // Add role to task if not already included
      if (!task.roles.includes(role)) {
        const { project: currentProject } = useProjectStore.getState();
        if (currentProject) {
          useProjectStore.getState().updateTask(task.id, {
            roles: [...task.roles, role]
          });
        }
      }
    }
  };

  const toggleSubtaskExpanded = (subtaskId: string) => {
    const newExpanded = new Set(expandedSubtasks);
    if (newExpanded.has(subtaskId)) {
      newExpanded.delete(subtaskId);
    } else {
      newExpanded.add(subtaskId);
    }
    setExpandedSubtasks(newExpanded);
  };

  const handleAddSubSubtask = (taskId: string, subtaskId: string) => {
    if (!newSubSubtaskText.trim()) {
      alert('請輸入細項內容');
      return;
    }

    addSubSubtask(taskId, subtaskId, newSubSubtaskText.trim());
    setNewSubSubtaskText('');
    setAddingSubSubtask(null);
    
    // Auto-expand the subtask to show the new sub-subtask
    const newExpanded = new Set(expandedSubtasks);
    newExpanded.add(subtaskId);
    setExpandedSubtasks(newExpanded);
  };

  const getSubSubtaskProgress = (subtask: Subtask) => {
    if (!subtask.subSubtasks || subtask.subSubtasks.length === 0) return null;
    const completed = subtask.subSubtasks.filter(s => s.completed).length;
    const total = subtask.subSubtasks.length;
    return `${completed}/${total} 細項`;
  };

  const handleEditSubtask = (taskId: string, subtask: Subtask) => {
    setEditSubtaskModal({ open: true, subtask, taskId });
  };

  const handleCloseEditModal = () => {
    setEditSubtaskModal({ open: false, subtask: null, taskId: '' });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: getRoleColor(role),
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">{role} 職能詳情</Typography>
          <Chip
            label={`${totalTasks} 個相關任務`}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontWeight: 600,
            }}
          />
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {/* Statistics Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 250px' }}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.light' }}>
                    <Assignment />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {totalSubtasks}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      總子任務數
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 250px' }}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.light' }}>
                    <CheckCircle />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {completionRate}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      完成率
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={completionRate}
                  sx={{ mt: 2, height: 6, borderRadius: 3 }}
                />
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 250px' }}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'info.light' }}>
                    <PersonAdd />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {personnelCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      參與人員
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 250px' }}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'warning.light' }}>
                    <TrendingUp />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {inProgressSubtasks}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      進行中
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Status Distribution */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            任務狀態分布
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 150px', textAlign: 'center' }}>
              <Typography variant="h5" color="text.secondary">
                {pendingSubtasks}
              </Typography>
              <Typography variant="body2">待執行</Typography>
            </Box>
            <Box sx={{ flex: '1 1 150px', textAlign: 'center' }}>
              <Typography variant="h5" color="info.main">
                {inProgressSubtasks}
              </Typography>
              <Typography variant="body2">執行中</Typography>
            </Box>
            <Box sx={{ flex: '1 1 150px', textAlign: 'center' }}>
              <Typography variant="h5" color="warning.main">
                {reviewingSubtasks}
              </Typography>
              <Typography variant="body2">審核中</Typography>
            </Box>
            <Box sx={{ flex: '1 1 150px', textAlign: 'center' }}>
              <Typography variant="h5" color="success.main">
                {completedSubtasks}
              </Typography>
              <Typography variant="body2">已完成</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Personnel List */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            參與人員
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Array.from(personnel).map((person) => (
              <Chip
                key={person}
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {person.charAt(0)}
                  </Avatar>
                }
                label={person}
                variant="outlined"
              />
            ))}
            {personnel.size === 0 && (
              <Typography variant="body2" color="text.secondary">
                尚未指派人員
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Add Subtask Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">相關任務與子任務</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setShowAddSubtask(true)}
          >
            新增子任務
          </Button>
        </Box>

        {/* Add Subtask Form */}
        {showAddSubtask && (
          <Paper sx={{ p: 3, mb: 3, border: '2px dashed', borderColor: 'primary.main' }}>
            <Typography variant="h6" gutterBottom>
              新增 {role} 子任務
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Autocomplete
                options={availableTasks}
                getOptionLabel={(option) => option.title}
                value={selectedTask}
                onChange={(_, newValue) => handleTaskSelect(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="選擇任務"
                    required
                    helperText={selectedTask && !selectedTask.roles.includes(role) 
                      ? `將自動為「${selectedTask.title}」添加 ${role} 職能` 
                      : ''}
                  />
                )}
              />
              
              <TextField
                fullWidth
                label="子任務描述"
                required
                value={newSubtaskData.text}
                onChange={(e) => setNewSubtaskData({ ...newSubtaskData, text: e.target.value })}
                placeholder={`例如：${role === 'UI' ? '設計登入頁面' : role === '前端' ? '實作表單驗證' : '建立API端點'}`}
              />
              
              <Autocomplete
                multiple
                freeSolo
                options={allAssignees}
                value={newSubtaskData.assignees}
                onChange={(_, newValue) => setNewSubtaskData({ ...newSubtaskData, assignees: newValue })}
                inputValue={assigneeInput}
                onInputChange={(_, newInputValue) => setAssigneeInput(newInputValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="負責人員"
                    placeholder="輸入名稱或選擇現有人員"
                    required
                    helperText="可輸入多個負責人，按 Enter 確認"
                  />
                )}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  sx={{ flex: 1 }}
                  type="date"
                  label="開始日期"
                  InputLabelProps={{ shrink: true }}
                  value={newSubtaskData.startDate}
                  onChange={(e) => setNewSubtaskData({ ...newSubtaskData, startDate: e.target.value })}
                />
                <TextField
                  sx={{ flex: 1 }}
                  type="date"
                  label="預計完成日期"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={newSubtaskData.expectedDate}
                  onChange={(e) => setNewSubtaskData({ ...newSubtaskData, expectedDate: e.target.value })}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button onClick={() => {
                  setShowAddSubtask(false);
                  setSelectedTask(null);
                  setNewSubtaskData({
                    taskId: '',
                    text: '',
                    assignees: [],
                    startDate: '',
                    expectedDate: '',
                  });
                }}>
                  取消
                </Button>
                <Button variant="contained" onClick={handleAddSubtask}>
                  新增子任務
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Tasks List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {roleTasks.map((task) => {
            const taskRoleSubtasks = task.subtasks.filter(st => st.role === role);
            const taskCompletedSubtasks = taskRoleSubtasks.filter(st => st.status === 'completed').length;
            const taskProgress = taskRoleSubtasks.length > 0
              ? Math.round((taskCompletedSubtasks / taskRoleSubtasks.length) * 100)
              : 0;

            return (
              <Paper key={task.id} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6">{task.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      PM: {task.pmName} | 預計完成: {formatDate(task.expectedDate)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={`${taskProgress}%`}
                      color={taskProgress === 100 ? 'success' : taskProgress > 50 ? 'warning' : 'default'}
                    />
                    <Button
                      size="small"
                      startIcon={<Add />}
                      onClick={() => {
                        setSelectedTask(task);
                        setNewSubtaskData({ ...newSubtaskData, taskId: task.id });
                        setShowAddSubtask(true);
                      }}
                    >
                      新增子任務
                    </Button>
                  </Box>
                </Box>

                {task.description && (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {task.description}
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  {role} 相關子任務 ({taskRoleSubtasks.length})
                </Typography>
                {taskRoleSubtasks.map((subtask) => {
                  const statusInfo = getStatusInfo(subtask.status);
                  const isExpanded = expandedSubtasks.has(subtask.id);
                  const isAddingSubSubtask = addingSubSubtask?.taskId === task.id && addingSubSubtask?.subtaskId === subtask.id;
                  const subSubtaskProgress = getSubSubtaskProgress(subtask);
                  
                  return (
                    <Box key={subtask.id}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          py: 1,
                          px: 2,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          mb: 1,
                          cursor: 'pointer',
                        }}
                        onClick={() => toggleSubtaskExpanded(subtask.id)}
                      >
                        <IconButton size="small" sx={{ p: 0 }}>
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: statusInfo.color,
                            color: statusInfo.textColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.875rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {statusInfo.icon}
                        </Box>
                        <Typography variant="body2" sx={{ 
                          flex: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            color: 'primary.main',
                            textDecoration: 'underline',
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSubtask(task.id, subtask);
                        }}
                        >
                          {subtask.text}
                        </Typography>
                        {subSubtaskProgress && (
                          <Chip
                            label={subSubtaskProgress}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                        {subtask.assignees.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {subtask.assignees.map((assignee) => (
                              <Chip
                                key={assignee}
                                label={assignee}
                                size="small"
                                avatar={
                                  <Avatar sx={{ width: 20, height: 20 }}>
                                    {assignee.charAt(0)}
                                  </Avatar>
                                }
                              />
                            ))}
                          </Box>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddingSubSubtask({ taskId: task.id, subtaskId: subtask.id });
                            setNewSubSubtaskText('');
                          }}
                          sx={{ ml: 1 }}
                        >
                          <AddTask fontSize="small" />
                        </IconButton>
                      </Box>

                      <Collapse in={isExpanded}>
                        <Box sx={{ ml: 7, mr: 2, mb: 2 }}>
                          {/* Progress bar for sub-subtasks */}
                          {subtask.subSubtasks && subtask.subSubtasks.length > 1 && (
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={(subtask.subSubtasks.filter(s => s.completed).length / subtask.subSubtasks.length) * 100}
                                  sx={{
                                    flex: 1,
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: 'action.hover',
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 3,
                                      backgroundColor: subtask.subSubtasks.every(s => s.completed) ? 'success.main' : 'primary.main',
                                    },
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 45 }}>
                                  {Math.round((subtask.subSubtasks.filter(s => s.completed).length / subtask.subSubtasks.length) * 100)}%
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          
                          {/* Sub-subtasks list */}
                          {subtask.subSubtasks && subtask.subSubtasks.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              {subtask.subSubtasks.map((subSubtask) => (
                                <Box
                                  key={subSubtask.id}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 0.5,
                                    px: 1,
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    mb: 0.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: '50%',
                                      bgcolor: subSubtask.completed ? 'success.main' : 'grey.300',
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.7rem',
                                      fontWeight: 'bold',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => useProjectStore.getState().toggleSubSubtask(task.id, subtask.id, subSubtask.id)}
                                  >
                                    {subSubtask.completed && '✓'}
                                  </Box>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      flex: 1,
                                      textDecoration: subSubtask.completed ? 'line-through' : 'none',
                                      color: subSubtask.completed ? 'text.secondary' : 'text.primary',
                                    }}
                                  >
                                    {subSubtask.text}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* Add sub-subtask form */}
                          {isAddingSubSubtask && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="輸入細項內容..."
                                value={newSubSubtaskText}
                                onChange={(e) => setNewSubSubtaskText(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddSubSubtask(task.id, subtask.id);
                                  }
                                }}
                                autoFocus
                              />
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleAddSubSubtask(task.id, subtask.id)}
                              >
                                新增
                              </Button>
                              <Button
                                size="small"
                                onClick={() => {
                                  setAddingSubSubtask(null);
                                  setNewSubSubtaskText('');
                                }}
                              >
                                取消
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}

                {taskRoleSubtasks.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    此任務尚無 {role} 相關的子任務
                  </Typography>
                )}
              </Paper>
            );
          })}

          {roleTasks.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                目前沒有包含 {role} 職能的任務
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                請先在任務中加入 {role} 職能，或使用上方「新增子任務」功能
              </Typography>
            </Paper>
          )}
        </Box>
      </DialogContent>

      {/* Edit Subtask Modal */}
      {editSubtaskModal.open && editSubtaskModal.subtask && editSubtaskModal.taskId && (
        <EditSubtaskModal
          open={editSubtaskModal.open}
          onClose={handleCloseEditModal}
          task={project.tasks.find(t => t.id === editSubtaskModal.taskId)!}
          subtask={editSubtaskModal.subtask}
          allAssignees={allAssignees}
        />
      )}
    </Dialog>
  );
};

export default RoleDetailModal; 